import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const DEFAULT_PAYMENT_TERMS = 'Credito 30 Dias';
const DEFAULT_VALIDITY_DAYS = 15;
const EXECUTIVE_ORG_RUT = '77.112.762-2';
const EXECUTIVE_ADDRESS = 'Anibal Pinto #340, Concepcion';
const CONTACT_FIELD_FONT_SIZE = 9;
const CONTACT_ROW_STEP = 4.4;
const CONTACT_ADDRESS_LINE_HEIGHT = 3.5;
const CONTACT_ADDRESS_MAX_LINES = 2;
const ORG_TITLE_FONT_SIZE = 14;

const clampTextToWidth = (doc, value, maxWidth) => {
  const textValue = String(value || 'S/I');
  if (doc.getTextWidth(textValue) <= maxWidth) return textValue;
  const ellipsis = '...';
  let end = textValue.length;
  while (end > 0 && doc.getTextWidth(`${textValue.slice(0, end)}${ellipsis}`) > maxWidth) {
    end -= 1;
  }
  return `${textValue.slice(0, Math.max(0, end)).trimEnd()}${ellipsis}`;
};

const drawContactLabelValue = (
  doc,
  x,
  y,
  label,
  value,
  maxX,
  { maxLines = 1, lineHeight = CONTACT_ADDRESS_LINE_HEIGHT } = {}
) => {
  doc.setFontSize(CONTACT_FIELD_FONT_SIZE);
  doc.setFont('helvetica', 'bold');
  const labelText = `${label}:`;
  doc.text(labelText, x, y);
  const labelWidth = doc.getTextWidth(labelText);
  doc.setFont('helvetica', 'normal');
  const valueX = x + labelWidth + 1;
  const maxValueWidth = Math.max(16, maxX - valueX);
  const wrapped = doc.splitTextToSize(String(value || 'S/I'), maxValueWidth).slice(0, maxLines);
  wrapped.forEach((line, index) => {
    doc.text(index === 0 ? ` ${line}` : line, index === 0 ? valueX : valueX, y + index * lineHeight);
  });
  const usedLines = Math.max(1, wrapped.length);
  return CONTACT_ROW_STEP + (usedLines - 1) * lineHeight;
};

const drawPartyContactBlock = (doc, x, maxX, startY, fields) => {
  let y = startY;
  fields.forEach((field) => {
    const step = drawContactLabelValue(doc, x, y, field.label, field.value, maxX, {
      maxLines: field.maxLines || 1,
      lineHeight: field.lineHeight || CONTACT_ADDRESS_LINE_HEIGHT,
    });
    y += step;
  });
  return y;
};

/**
 * Encabezado de bloques Soquimat / Cliente en el PDF de cotización.
 */
export function drawQuotationContactHeader(doc, {
  pageWidth,
  organizationLabel = 'Cliente',
  clientOrgRut = 'S/I',
  contactName = '-',
  clientEmail = 'S/I',
  clientPhone = 'S/I',
  clientAddress = 'S/I',
  executiveName = 'S/I',
  executiveEmail = 'S/I',
  executivePhone = 'S/I',
  executiveAddress = EXECUTIVE_ADDRESS,
  executiveOrgRut = EXECUTIVE_ORG_RUT,
  detailsTopY = 40,
} = {}) {
  const leftX = 18;
  const rightX = pageWidth / 2 + 10;
  const leftDividerX = leftX - 2;
  const rightDividerX = rightX - 4;
  const leftBlockMaxX = rightDividerX - 2;
  const rightBlockMaxX = pageWidth - 14;
  const contactStartY = detailsTopY + 14;
  const orgTitleMaxWidth = {
    left: leftBlockMaxX - leftX,
    right: rightBlockMaxX - rightX,
  };

  doc.setDrawColor(90, 90, 90);
  doc.setLineWidth(0.4);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(ORG_TITLE_FONT_SIZE);
  doc.text(clampTextToWidth(doc, 'Soquimat SpA', orgTitleMaxWidth.left), leftX, detailsTopY + 4);
  doc.text(
    clampTextToWidth(doc, organizationLabel || 'Cliente', orgTitleMaxWidth.right),
    rightX,
    detailsTopY + 4
  );

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.8);
  doc.setTextColor(130, 130, 130);
  doc.text(`${executiveOrgRut}`, leftX, detailsTopY + 9);
  doc.text(`${clientOrgRut}`, rightX, detailsTopY + 9);
  doc.setTextColor(0, 0, 0);

  const contactFields = (name, email, phone, address) => [
    { label: 'Nombre', value: name },
    { label: 'Email', value: email },
    { label: 'Telefono', value: phone },
    {
      label: 'Direccion',
      value: address,
      maxLines: CONTACT_ADDRESS_MAX_LINES,
      lineHeight: CONTACT_ADDRESS_LINE_HEIGHT,
    },
  ];

  const leftBottomY = drawPartyContactBlock(
    doc,
    leftX,
    leftBlockMaxX,
    contactStartY,
    contactFields(executiveName, executiveEmail, executivePhone, executiveAddress)
  );
  const rightBottomY = drawPartyContactBlock(
    doc,
    rightX,
    rightBlockMaxX,
    contactStartY,
    contactFields(contactName, clientEmail, clientPhone, clientAddress)
  );
  const detailsBottomY = Math.max(leftBottomY, rightBottomY) + 2;

  doc.line(leftDividerX, detailsTopY, leftDividerX, detailsBottomY);
  doc.line(rightDividerX, detailsTopY, rightDividerX, detailsBottomY);

  return { detailsBottomY, leftX, rightX, leftBlockMaxX, rightBlockMaxX };
}

const resolveValidityDays = (value, fallback = DEFAULT_VALIDITY_DAYS) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.round(n);
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const loadLogoDataUrl = () =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.width || 220;
        canvas.height = image.height || 90;
        const context = canvas.getContext('2d');
        if (!context) {
          resolve(null);
          return;
        }
        context.drawImage(image, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (error) {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = '/assets/soquimat-logo.png';
  });

const ensurePdfVerticalSpace = (doc, y, neededMm, marginBottom = 16, marginTop = 16) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + neededMm <= pageHeight - marginBottom) return y;
  doc.addPage();
  return marginTop;
};

/**
 * Genera el PDF de cotización emitida (mismo layout que al emitir desde Cotizaciones).
 * @returns {Promise<Blob>}
 */
export async function buildQuotationPdfBlob(input = {}) {
  const items = Array.isArray(input.items) ? input.items : [];
  const pdfFormData = {
    quotation_code: input.quotation_code || '',
    quotation_date: input.quotation_date || todayISO(),
    validity_days: resolveValidityDays(input.validity_days),
    payment_terms: input.payment_terms || DEFAULT_PAYMENT_TERMS,
    delivery_days: Number(input.delivery_days || 0),
    delivery_method: String(input.delivery_method || '').trim() || 'Despacho',
    delivery_place: input.delivery_place || '',
    channel: String(input.channel || '').trim(),
    tender: String(input.tender || '').trim(),
    comments_text: String(input.comments || '')
      .split(/[;\n]/)
      .map((line) => line.trim())
      .filter(Boolean)
      .join('\n'),
  };

  const organizationLabel = input.organization_name || 'Cliente';
  const clientOrgRut = input.organization_rut || 'S/I';
  const contactLabel = input.contact_name || '-';
  const clientAddress = input.client_address || 'S/I';
  const clientEmail = input.contact_email || 'S/I';
  const clientPhone = input.contact_phone || 'S/I';
  const executiveName = input.executive_name || 'S/I';
  const executiveEmail = input.executive_email || 'S/I';
  const executivePhone = input.executive_phone || 'S/I';

  const itemsForPdf = items.map((item) => {
    const quantity = Number(item.quantity || 0);
    const unitPrice = Number(item.unit_price || 0);
    const discount = Number(item.discount_percent || 0);
    const lineTotal =
      item.line_total != null
        ? Number(item.line_total)
        : quantity * unitPrice * (1 - Math.max(Math.min(discount, 100), 0) / 100);
    return {
      item_name: item.item_name || '',
      color: item.color || '',
      quantity,
      unit: item.unit || '',
      unit_price: unitPrice,
      discount_percent: discount,
      line_total: lineTotal,
    };
  });

  const total = itemsForPdf.reduce((acc, item) => acc + Number(item.line_total || 0), 0);
  const subtotalAmount = Math.round(total);
  const ivaAmount = Math.round(total * 0.19);
  const grandTotalAmount = subtotalAmount + ivaAmount;
  const totalQuotedUnits = itemsForPdf.reduce((acc, item) => acc + Number(item.quantity || 0), 0);

  const commentsLines = (pdfFormData.comments_text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  if (pdfFormData.channel && pdfFormData.channel.toLowerCase() !== 'directo') {
    const channelComment = pdfFormData.tender
      ? `Cotización correspondiente al canal ${pdfFormData.channel}, requerimiento ${pdfFormData.tender}.`
      : `Cotización correspondiente al canal ${pdfFormData.channel}.`;
    commentsLines.unshift(channelComment);
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const logoDataUrl = await loadLogoDataUrl();
  const headerRightX = pageWidth - 18;
  const logoTopY = 5.5;
  const headerBlockBaseY = logoTopY + 10;
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', 14, logoTopY, 67.6, 31.2);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(`COTIZACION N° ${pdfFormData.quotation_code || 'Por asignar'}`, headerRightX, headerBlockBaseY, {
    align: 'right',
  });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Emitida el: ${pdfFormData.quotation_date || todayISO()}`, headerRightX, headerBlockBaseY + 6, {
    align: 'right',
  });
  doc.text(`Validez: ${resolveValidityDays(pdfFormData.validity_days)} dias`, headerRightX, headerBlockBaseY + 11, {
    align: 'right',
  });

  const detailsTopY = 40;
  const { detailsBottomY } = drawQuotationContactHeader(doc, {
    pageWidth,
    organizationLabel,
    clientOrgRut,
    contactName: contactLabel,
    clientEmail,
    clientPhone,
    clientAddress,
    executiveName,
    executiveEmail,
    executivePhone,
    detailsTopY,
  });

  const drawWrappedInfoLine = (x, y, label, value, blockWidth = 118, lineHeight = 4.1) => {
    const labelText = `${label}:`;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.4);
    doc.text(labelText, x, y);
    const labelWidth = doc.getTextWidth(labelText);
    const valueX = x + labelWidth + 1.2;
    const availableWidth = Math.max(20, blockWidth - (valueX - x));
    doc.setFont('helvetica', 'normal');
    const wrappedValue = doc.splitTextToSize(String(value || 'S/I'), availableWidth);
    if (wrappedValue.length > 0) doc.text(wrappedValue[0], valueX, y);
    for (let i = 1; i < wrappedValue.length; i += 1) {
      doc.text(wrappedValue[i], valueX, y + i * lineHeight);
    }
    return Math.max(1, wrappedValue.length) * lineHeight;
  };

  const itemRows = itemsForPdf.map((item, idx) => [
    idx + 1,
    item.item_name || '',
    item.color || '-',
    Number(item.quantity || 0).toLocaleString('es-CL'),
    item.unit || '',
    `$ ${Number(item.unit_price || 0).toLocaleString('es-CL')}`,
    `${Number(item.discount_percent || 0)}%`,
    `$ ${Number(item.line_total || 0).toLocaleString('es-CL')}`,
  ]);

  const preTableTextY = detailsBottomY + 9;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.2);
  const preTableText = `Mediante este documento tenemos el agrado de cotizar ${Number(totalQuotedUnits).toLocaleString('es-CL')} productos según detalle a continuación:`;
  const preTableLines = doc.splitTextToSize(preTableText, pageWidth - 28);
  doc.text(preTableLines, 14, preTableTextY, { lineHeightFactor: 1.45 });

  autoTable(doc, {
    startY: preTableTextY + preTableLines.length * 4.6 + 3,
    head: [['ID', 'Producto', 'Color', 'Cant.', 'Formato', 'Unitario', 'Dcto', 'Total']],
    body: itemRows.length
      ? itemRows
      : [['—', 'Sin ítems', '—', '—', '—', '—', '—', '—']],
    showHead: 'everyPage',
    styles: { fontSize: 6.5, cellPadding: 1.2, halign: 'center', overflow: 'linebreak' },
    headStyles: {
      fillColor: [20, 30, 44],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 7,
      cellPadding: 1.4,
    },
    bodyStyles: { fontSize: 6.5, cellPadding: 1.2 },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 'auto', halign: 'left' },
      2: { cellWidth: 24, halign: 'center' },
      3: { cellWidth: 14, halign: 'center' },
      4: { cellWidth: 22, halign: 'center' },
      5: { cellWidth: 22, halign: 'center' },
      6: { cellWidth: 12, halign: 'center' },
      7: { cellWidth: 24, halign: 'right' },
    },
    margin: { left: 14, right: 14, bottom: 16 },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 1) {
        data.cell.styles.halign = 'left';
      }
    },
  });

  const summaryBlockNeeded = 34;
  let blockTop = (doc.lastAutoTable?.finalY || detailsBottomY + 40) + 6;
  blockTop = ensurePdfVerticalSpace(doc, blockTop, summaryBlockNeeded);

  const totalsColWidth = 64;
  const totalsColX = pageWidth - 14 - totalsColWidth;
  const infoBlockWidth = Math.max(70, totalsColX - 14 - 8);
  const infoStep = 4.2;

  autoTable(doc, {
    startY: blockTop,
    margin: { left: totalsColX, right: 14 },
    tableWidth: totalsColWidth,
    body: [
      [
        { content: 'SUBTOTAL', styles: { halign: 'right', fontStyle: 'bold', fillColor: [247, 247, 247] } },
        {
          content: `$ ${subtotalAmount.toLocaleString('es-CL')}`,
          styles: { halign: 'right', fontStyle: 'bold', fillColor: [247, 247, 247] },
        },
      ],
      [
        { content: 'IVA (19%)', styles: { halign: 'right', fontStyle: 'bold', fillColor: [247, 247, 247] } },
        {
          content: `$ ${ivaAmount.toLocaleString('es-CL')}`,
          styles: { halign: 'right', fontStyle: 'bold', fillColor: [247, 247, 247] },
        },
      ],
      [
        { content: 'TOTAL', styles: { halign: 'right', fontStyle: 'bold', fillColor: [235, 235, 235] } },
        {
          content: `$ ${grandTotalAmount.toLocaleString('es-CL')}`,
          styles: { halign: 'right', fontStyle: 'bold', fillColor: [235, 235, 235] },
        },
      ],
    ],
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: 1.6, textColor: [0, 0, 0] },
    columnStyles: {
      0: { cellWidth: 28 },
      1: { cellWidth: 36 },
    },
  });
  const totalsBottomY = doc.lastAutoTable?.finalY || blockTop + 20;

  let infoY = blockTop + 4;
  drawWrappedInfoLine(
    14,
    infoY,
    'Validez Cotizacion',
    `${resolveValidityDays(pdfFormData.validity_days)} Dias`,
    infoBlockWidth,
    infoStep
  );
  infoY += infoStep;
  drawWrappedInfoLine(
    14,
    infoY,
    'Forma de Pago',
    pdfFormData.payment_terms || DEFAULT_PAYMENT_TERMS,
    infoBlockWidth,
    infoStep
  );
  infoY += infoStep;
  drawWrappedInfoLine(
    14,
    infoY,
    'Plazo de Entrega',
    `${pdfFormData.delivery_days || 0} Dias`,
    infoBlockWidth,
    infoStep
  );
  infoY += infoStep;
  const lastInfoHeight = drawWrappedInfoLine(
    14,
    infoY,
    'Método de Entrega',
    pdfFormData.delivery_method,
    infoBlockWidth,
    infoStep
  );
  let cursorY = Math.max(infoY + lastInfoHeight, totalsBottomY) + 6;

  const commentsText = commentsLines.length
    ? commentsLines.map((line, index) => `${index + 1}) ${line}`).join('\n')
    : 'Sin comentarios.';
  const commentBoxWidth = pageWidth - 28;
  const commentPadX = 3.5;
  const commentPadTop = 4.5;
  const commentFontSize = 7.2;
  const commentLineHeight = 3.6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(commentFontSize);
  const wrappedComments = doc.splitTextToSize(
    commentsText,
    Math.max(20, commentBoxWidth - commentPadX * 2)
  );
  const commentBoxHeight = Math.max(16, commentPadTop + 2 + wrappedComments.length * commentLineHeight);
  cursorY = ensurePdfVerticalSpace(doc, cursorY + 2, 8 + commentBoxHeight + 20);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Comentarios:', 14, cursorY);
  const commentBoxX = 14;
  const commentBoxY = cursorY + 2;
  doc.setDrawColor(90, 90, 90);
  doc.setLineWidth(0.35);
  doc.rect(commentBoxX, commentBoxY, commentBoxWidth, commentBoxHeight);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(commentFontSize);
  doc.text(wrappedComments, commentBoxX + commentPadX, commentBoxY + commentPadTop, {
    lineHeightFactor: 1.25,
  });

  const paymentInfoY = ensurePdfVerticalSpace(doc, commentBoxY + commentBoxHeight + 7, 14);
  doc.setFontSize(8.2);
  doc.setFont('helvetica', 'bold');
  doc.text('Datos de transferencia:', 14, paymentInfoY);
  doc.setFont('helvetica', 'normal');
  doc.text(
    'Razon Social: SOQUIMAT SpA | RUT: 77.112.762-2 | Banco de Chile | N° Cta Corriente: 00-225-55423-02',
    14,
    paymentInfoY + 4.2
  );
  doc.text('Envio comprobante: finanzas@soquimat.cl', 14, paymentInfoY + 8.4);

  return doc.output('blob');
}

export async function buildQuotationPdfObjectUrl(input = {}) {
  const blob = await buildQuotationPdfBlob(input);
  return URL.createObjectURL(blob);
}

/**
 * Nombre de archivo por defecto al emitir PDF de cotización.
 * Ej: "261073 - Cotización. I. Municipalidad de Puerto Montt. Los Lagos.pdf"
 * Usa el correlativo de cotización (sin prefijo Q) al inicio; no repite el código al final.
 */
export function buildQuotationPdfFilename({
  organization_name = '',
  region = '',
  quotation_code = '',
} = {}) {
  const rawCode = String(quotation_code || '').trim();
  const quoteDigits = rawCode.replace(/^Q/i, '').trim() || 'cotizacion';
  const orgName = String(organization_name || '').trim() || 'Cliente';
  const regionLabel = String(region || '').trim();

  const parts = [`${quoteDigits} - Cotización. ${orgName}.`];
  if (regionLabel) parts.push(regionLabel);

  const base = parts.join(' ').replace(/\s+/g, ' ').trim();
  // Caracteres inválidos en nombres de archivo (Windows/macOS); se conservan espacios y acentos.
  const safe = base.replace(/[\\/:*?"<>|]/g, '-').replace(/\.+$/g, '');
  return `${safe || 'cotizacion'}.pdf`;
}
